import { useEffect, useState } from "react";
import AddIcon from "@mui/icons-material/Add";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
  Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Tooltip,
} from "@mui/material";
import { EditorSection, isSectionEmpty, sectionDisplayName } from "./sectionUtils";
import QuoteRemoveSectionDialog from "./QuoteRemoveSectionDialog";
import "./QuoteSectionBar.scss";

type Props = {
  open: boolean;
  sections: EditorSection[];
  activeIndex: number;
  onClose: () => void;
  onJump: (index: number) => void;
  onMove: (from: number, to: number) => void;
  onAdd: () => void;
  onRemove: (index: number) => void;
};

const QuoteSectionsModal = ({ open, sections, activeIndex, onClose, onJump, onMove, onAdd, onRemove }: Props) => {
  const [confirmRemove, setConfirmRemove] = useState<number | null>(null);
  useEffect(() => { if (!open) setConfirmRemove(null); }, [open]);
  const removing = confirmRemove != null ? sections[confirmRemove] : null;

  return <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
    <DialogTitle>Manage Sections</DialogTitle>
    <DialogContent dividers>
      <Box className="quote-sections-list">
        {sections.map((section, index) => <Box key={section.key}
          className={`quote-sections-row${index === activeIndex ? " is-active" : ""}`}>
          <Box className="quote-sections-move">
            <Tooltip enterDelay={1800} title="Move section up"><span><IconButton size="small" aria-label={`Move ${sectionDisplayName(section.name, index)} up`}
              disabled={index === 0} onClick={() => onMove(index, index - 1)}><ArrowUpwardIcon fontSize="small" /></IconButton></span></Tooltip>
            <Tooltip enterDelay={1800} title="Move section down"><span><IconButton size="small" aria-label={`Move ${sectionDisplayName(section.name, index)} down`}
              disabled={index === sections.length - 1} onClick={() => onMove(index, index + 1)}><ArrowDownwardIcon fontSize="small" /></IconButton></span></Tooltip>
          </Box>
          <Button className="quote-sections-jump" onClick={() => { onJump(index); onClose(); }}>
            <span className="quote-sections-name">{sectionDisplayName(section.name, index)}</span>
            {index === activeIndex && <Chip size="small" label="Active" color="primary" />}
            {isSectionEmpty(section) && <Chip size="small" label="Empty" color="warning" variant="outlined" />}
          </Button>
          <Tooltip enterDelay={1800} title={sections.length === 1 ? "A quote must keep at least one section" : "Remove this section"}>
            <span><IconButton size="small" aria-label={`Remove ${sectionDisplayName(section.name, index)}`}
              disabled={sections.length === 1} onClick={() => setConfirmRemove(index)}><DeleteOutlineIcon fontSize="small" /></IconButton></span>
          </Tooltip>
        </Box>)}
      </Box>
      <Button className="quote-sections-add" startIcon={<AddIcon />} onClick={onAdd}>Add Section</Button>
    </DialogContent>
    <DialogActions>
      <Button color="inherit" onClick={onClose}>Close</Button>
    </DialogActions>

    <QuoteRemoveSectionDialog section={removing} index={confirmRemove} onCancel={() => setConfirmRemove(null)}
      onConfirm={index => { onRemove(index); setConfirmRemove(null); }} />
  </Dialog>;
};

export default QuoteSectionsModal;
